// ═══════════════════════════════════════════════════════════════
// AdminErrors — every crash, grouped into bugs.
//
// The point of recording crashes was never the recording. It was
// being able to answer "is anything broken right now?" without
// opening the Firebase console and reading raw documents. So this
// page leads with the count of distinct bugs that are still open, not
// with a wall of stack traces.
//
// Grouped by fingerprint: one bad render loop writes twenty reports
// that are all the same bug, and twenty rows of the same thing is a
// list nobody reads twice.
// ═══════════════════════════════════════════════════════════════
import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, Check, Filter, ChevronDown, ChevronRight,
  Users, Smartphone, RotateCcw,
} from 'lucide-react';
import {
  useErrorReports, groupReports, markGroupHandled, REPORT_LIMIT,
} from './lib/use-error-reports.js';
import { toDate } from './lib/job-helpers.js';
import { APP_VERSION } from './version.js';

function when(ts) {
  const d = toDate(ts);
  if (!d) return 'unknown';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function ago(date) {
  if (!date) return '';
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// "Mozilla/5.0 (iPhone; CPU iPhone OS 17_4…" is unreadable in a list.
// The browser and the platform are what actually narrow a bug down.
function device(ua) {
  if (!ua) return '';
  const platform =
    /iPhone|iPad/.test(ua) ? 'iOS'
    : /Android/.test(ua)   ? 'Android'
    : /Macintosh/.test(ua) ? 'Mac'
    : /Windows/.test(ua)   ? 'Windows'
    : '';
  const browser =
    /Edg\//.test(ua)     ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : '';
  return [platform, browser].filter(Boolean).join(' ');
}

export default function AdminErrors() {
  const { reports, ready, error } = useErrorReports();
  const [filter, setFilter] = useState('open');
  const [expanded, setExpanded] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [actionError, setActionError] = useState(null);

  const groups = useMemo(() => groupReports(reports), [reports]);
  const openGroups = useMemo(() => groups.filter((g) => !g.handled), [groups]);

  const shown = useMemo(() => {
    if (filter === 'all') return groups;
    if (filter === 'handled') return groups.filter((g) => g.handled);
    return openGroups;
  }, [groups, openGroups, filter]);

  async function toggleHandled(group) {
    setBusyKey(group.key);
    setActionError(null);
    try {
      await markGroupHandled(group, APP_VERSION, !group.handled);
    } catch (e) {
      console.error('Could not update the report:', e);
      setActionError(e.message);
    } finally {
      setBusyKey(null);
    }
  }

  const tabs = [
    { key: 'open',    label: `Open (${openGroups.length})` },
    { key: 'handled', label: `Handled (${groups.length - openGroups.length})` },
    { key: 'all',     label: `All (${groups.length})` },
  ];

  return (
    <div className="fb-admin">
      <div className="screen-header">
        <div>
          <h1>Crashes</h1>
          <p className="subtitle">
            {!ready ? 'Loading…'
              : openGroups.length === 0
                ? 'Nothing broken right now.'
                : `${openGroups.length} open ${openGroups.length === 1 ? 'bug' : 'bugs'}, ${reports.length} report${reports.length === 1 ? '' : 's'}`}
          </p>
        </div>
      </div>

      {error && (
        <p className="form-error">Could not load crash reports: {error.message}</p>
      )}
      {actionError && <p className="form-error">{actionError}</p>}

      <div className="fb-filter-row" role="tablist" aria-label="Filter crashes">
        <Filter size={13} className="fb-filter-icon" />
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={filter === t.key}
            className={`fb-filter-chip${filter === t.key ? ' active' : ''}`}
            onClick={() => setFilter(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {ready && shown.length === 0 && (
        <p className="muted small empty-inline">
          {filter === 'open' ? 'No open crashes. ' : 'Nothing here. '}
          {reports.length === 0 && 'Nothing has crashed since reporting was switched on.'}
        </p>
      )}

      <ul className="err-list">
        {shown.map((g) => {
          const open = expanded === g.key;
          const s = g.sample;
          return (
            <li key={g.key} className={`err-item ${g.handled ? 'handled' : ''}`}>
              <button
                type="button"
                className="err-head"
                onClick={() => setExpanded(open ? null : g.key)}
                aria-expanded={open}
              >
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="err-head-body">
                  <span className="err-title">
                    <strong>{s.name || 'Error'}</strong>
                    {s.message ? <> — {s.message}</> : null}
                  </span>
                  <span className="muted small err-meta">
                    <span className="err-count">×{g.count}</span>
                    {' · '}{g.context || 'unknown'}
                    {' · last '}{ago(g.lastSeen)}
                    {g.users.length > 1 && (
                      <> · <Users size={11} style={{ verticalAlign: '-1px' }} /> {g.users.length} people</>
                    )}
                    {g.envs.includes('sandbox') && g.envs.length === 1 && <> · sandbox only</>}
                  </span>
                </span>
              </button>

              {open && (
                <div className="err-body">
                  <dl className="err-facts">
                    <div><dt>First seen</dt><dd>{when(g.firstSeen)}</dd></div>
                    <div><dt>Last seen</dt><dd>{when(g.lastSeen)}</dd></div>
                    <div><dt>Builds</dt><dd className="mono">{g.versions.join(', ') || '—'}</dd></div>
                    <div><dt>Where</dt><dd>{[...new Set(g.paths)].join(', ') || '—'}</dd></div>
                    <div>
                      <dt><Smartphone size={11} style={{ verticalAlign: '-1px' }} /> Device</dt>
                      <dd>{device(s.userAgent) || '—'}{s.online === false && ' · offline'}</dd>
                    </div>
                    <div><dt>Who</dt><dd>{g.users.length === 1 ? (s.userEmail || s.userId) : `${g.users.length} people`}</dd></div>
                  </dl>

                  {s.stack && <pre className="err-stack">{s.stack}</pre>}

                  {g.handled && (
                    <p className="muted small">
                      Marked handled
                      {s.handledVersion ? <> on <span className="mono">{s.handledVersion}</span></> : null}.
                      It'll come back to Open if it happens again.
                    </p>
                  )}

                  <div className="err-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={busyKey === g.key}
                      onClick={() => toggleHandled(g)}
                    >
                      {g.handled
                        ? <><RotateCcw size={13} /> Reopen</>
                        : <><Check size={13} /> Mark handled</>}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {reports.length >= REPORT_LIMIT && (
        <p className="muted small err-note">
          <AlertTriangle size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
          Showing the most recent {REPORT_LIMIT} reports. Older ones are still in Firestore.
        </p>
      )}

      <p className="muted small err-note">
        Crashes on the public estimate and invoice pages aren't recorded — those visitors
        aren't signed in, and an unauthenticated write path here would be a spam target.
        The customer still sees a recovery screen rather than a blank page.
      </p>
    </div>
  );
}
